import { useEffect, useState, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Car, ShoppingCart, ArrowUpDown, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import AddressAliasesDialog from "@/components/AddressAliasesDialog";
import { naplesLocalToUtcISO, nowNaplesLocalString } from "@/lib/naplesTime";
import { STATUS_LABELS } from "@/lib/status";
import TripMovementsSection from "@/components/TripMovementsSection";

const emptyTrip = {
  passenger_name: "",
  passenger_phone: "",
  pickup_address: "",
  pickup_lat: "",
  pickup_lng: "",
  pickup_place_id: null,
  dropoff_address: "",
  dropoff_lat: "",
  dropoff_lng: "",
  dropoff_place_id: null,
  price: "",
  scheduled_at: "",
  lead_minutes: "",  // "" = crear inmediato · 15/30/…/120 = guardar como cita
  driver_id: "",
  payment_method: "cash",
  notes: "",
  trip_type: "trip",
  items: "",
  driver_income: "",
  llc_income: "",
  driver_collected: "",
  driver_investment: "",
  client_debt_payment: "",
  client_credit_applied: "",
};

export default function TripFormDialog({ open, onOpenChange, trip, drivers, onSaved }) {
  const [form, setForm] = useState(emptyTrip);
  const [busy, setBusy] = useState(false);
  const [aliases, setAliases] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [previousReasons, setPreviousReasons] = useState([]);
  // Duplicate-phone confirmation: when the dispatcher tries to create a trip
  // for a phone that ALREADY has an active trip (pending / assigned /
  // accepted / …), we pause the submit and ask for explicit confirmation
  // before creating a second one. Avoids typos and accidental doubles.
  const [dupConfirm, setDupConfirm] = useState({ open: false, trips: [] });
  // Ledger summary for the passenger phone entered above. Fetched with a
  // debounce so we don't hammer the API on every keystroke.
  const [clientSummary, setClientSummary] = useState(null);
  // Quick-history popover: last N completed trips for the phone typed above.
  // Opened by the "Historial rápido" link next to the phone label.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTrips, setHistoryTrips] = useState([]);
  const [historyPhone, setHistoryPhone] = useState("");
  const isEdit = !!trip;

  const loadAliases = useCallback(async () => {
    try {
      const { data } = await api.get("/aliases");
      setAliases(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn("TripFormDialog.loadAliases failed:", e);
    }
  }, []);

  useEffect(() => {
    if (open) loadAliases();
  }, [open, loadAliases]);

  // Look up the passenger by phone number to prefill the name and show any
  // outstanding balance with the LLC. Debounced so we don't fire on every
  // keystroke. Runs on both create AND edit so that if the dispatcher fixes
  // a missing/wrong phone on an existing trip, the client gets detected too.
  // Name that THIS form auto-filled from a phone lookup. Lets us drop/replace
  // it when the dispatcher erases or changes the phone, so a wrong number never
  // leaves a stale name behind (which used to create a duplicate client).
  const autoNameRef = useRef("");
  // Concurrency: did THIS editor touch the Conductor select? If not, the
  // driver assigned meanwhile by someone else must win (and be shown live).
  const driverTouchedRef = useRef(false);
  const [liveDriverNote, setLiveDriverNote] = useState("");
  const [liveHistory, setLiveHistory] = useState(null);
  useEffect(() => {
    if (!open) return;
    const phone = (form.passenger_phone || "").trim();
    if (phone.length < 6) {
      setClientSummary(null);
      if (phone.length === 0) {
        const wasAuto = autoNameRef.current && form.passenger_name === autoNameRef.current;
        const wasTripName = isEdit && form.passenger_name && form.passenger_name === (trip?.passenger_name || "");
        autoNameRef.current = "";
        if (wasAuto || wasTripName || (!isEdit && form.passenger_name)) setForm((f) => ({ ...f, passenger_name: "" }));
      }
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients/lookup`, { params: { phone } });
        setClientSummary(data);
        // Auto-fill when the dispatcher hasn't typed a name, or when the
        // current name is one we auto-filled for a previous phone.
        // (No side effects inside the updater: StrictMode double-invokes it.)
        const currentName = form.passenger_name;
        const origDigits = (trip?.passenger_phone || "").replace(/\D/g, "");
        const phoneChangedOnEdit = isEdit && origDigits && phone.replace(/\D/g, "") !== origDigits;
        // On edit, the name that came with the trip belongs to the OLD phone:
        // once the dispatcher types a different number it must be replaced too.
        const replaceable = !currentName
          || currentName === autoNameRef.current
          || (phoneChangedOnEdit && currentName === (trip?.passenger_name || ""));
        if (data?.passenger_name && replaceable) {
          autoNameRef.current = data.passenger_name;
          setForm((f) => ({ ...f, passenger_name: data.passenger_name }));
        }
      } catch (err) {
        // Silent by design — the ledger lookup is a best-effort UX helper
        // (autofill name, show balance chip) and must never block trip
        // creation. Log only in dev so we can spot repeated failures.
        if (process.env.NODE_ENV !== "production") console.debug("client lookup failed:", err?.message || err);
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.passenger_phone]);

  // Same-name lookup: warn when the dispatcher types a passenger_name that
  // already exists in the clients registry under a DIFFERENT phone number.
  // Only fires on create (isEdit=false). Debounced.
  const [nameConflicts, setNameConflicts] = useState([]);
  useEffect(() => {
    if (!open || isEdit) { setNameConflicts([]); return; }
    const name = (form.passenger_name || "").trim();
    if (name.length < 2) { setNameConflicts([]); return; }
    const normalizedPhone = (form.passenger_phone || "").replace(/\D/g, "");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients/lookup-by-name`, { params: { name } });
        const others = (Array.isArray(data) ? data : [])
          .filter((c) => (c.phone || "").replace(/\D/g, "") !== normalizedPhone);
        setNameConflicts(others);
      } catch (err) {
        // Non-fatal — never block trip creation on a UX lookup failure.
        if (process.env.NODE_ENV !== "production") console.debug("name lookup failed:", err?.message || err);
        setNameConflicts([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [open, isEdit, form.passenger_name, form.passenger_phone]);

  // Safety net: as soon as the dispatcher picks a future `scheduled_at`, we
  // auto-select `lead_minutes = 15` so the trip is stored as a Cita by
  // default. Before this, the default option ("Crear ahora") was easy to
  // overlook and dispatchers were creating instant trips when they intended
  // to schedule a cita. If they truly want an immediate trip, they must
  // explicitly pick "Crear ahora (bajar inmediato)" from the dropdown.
  useEffect(() => {
    if (!open || isEdit) return;
    if (!form.scheduled_at) return;
    if (form.lead_minutes) return;              // dispatcher already chose
    // Only auto-fill when the picked time is in the future — a past date
    // means they're back-dating, so keep it as immediate creation and let
    // them pick explicitly.
    // Interpret the input as Naples wallclock (same rule as when we save),
    // so a dispatcher in another timezone still sees "future" correctly.
    const schedIso = naplesLocalToUtcISO(form.scheduled_at);
    const sched = schedIso ? new Date(schedIso) : null;
    if (!sched || Number.isNaN(sched.getTime()) || sched.getTime() <= Date.now() + 60_000) return;
    setForm((f) => (f.lead_minutes ? f : { ...f, lead_minutes: "15" }));
  }, [open, isEdit, form.scheduled_at, form.lead_minutes]);

  // Keep payment_method in sync with credit coverage: if the applied credit
  // fully covers the price, force `credito`; if it drops below, roll back to
  // cash unless the dispatcher already picked something else.
  useEffect(() => {
    const applied = Number(form.client_credit_applied || 0);
    const price = Number(form.price || 0);
    if (applied <= 0.005 || price <= 0) return;
    const covers = applied >= price - 0.005;
    setForm((f) => {
      if (covers && f.payment_method !== "credito") return { ...f, payment_method: "credito" };
      if (!covers && f.payment_method === "credito") return { ...f, payment_method: "cash" };
      return f;
    });
  }, [form.client_credit_applied, form.price]);

  useEffect(() => {
    autoNameRef.current = "";
    driverTouchedRef.current = false;
    setLiveDriverNote("");
    if (trip) {
      setForm({
        passenger_name: trip.passenger_name || "",
        passenger_phone: trip.passenger_phone || "",
        pickup_address: trip.pickup_address || "",
        pickup_lat: trip.pickup_lat ?? "",
        pickup_lng: trip.pickup_lng ?? "",
        pickup_place_id: trip.pickup_place_id || null,
        dropoff_address: trip.dropoff_address || "",
        dropoff_lat: trip.dropoff_lat ?? "",
        dropoff_lng: trip.dropoff_lng ?? "",
        dropoff_place_id: trip.dropoff_place_id || null,
        price: trip.price ?? "",
        scheduled_at: trip.scheduled_at || "",
        lead_minutes: "",  // never resume with a lead — editing an existing trip is instant
        driver_id: trip.driver_id || "",
        payment_method: trip.payment_method || "cash",
        notes: trip.notes || "",
        trip_type: trip.trip_type || "trip",
        items: trip.items || "",
        driver_income: trip.driver_income ?? "",
        llc_income: trip.llc_income ?? "",
        driver_collected: trip.driver_collected ?? "",
        driver_investment: trip.driver_investment ?? "",
        client_debt_payment: trip.client_debt_payment ?? "",
        client_credit_applied: trip.client_credit_applied ?? "",
      });
    } else {
      setForm(emptyTrip);
    }
    setClientSummary(null);
    // Capture the trip's current inconsistency reasons so we can announce
    // "ya no está en la lista de huérfanos" when the dispatcher fixes it.
    if (open && trip?.id) {
      api.get(`/trips/${trip.id}/issues`)
        .then(({ data }) => setPreviousReasons(Array.isArray(data?.reasons) ? data.reasons : []))
        .catch(() => setPreviousReasons([]));
    } else {
      setPreviousReasons([]);
    }
  }, [trip, open]);

  // While editing, watch the trip on the server: if another dispatcher (or
  // the driver himself) assigns/accepts it, reflect it in "Conductor" unless
  // this editor already changed that field on purpose.
  useEffect(() => {
    if (!open || !trip?.id) { setLiveHistory(null); return undefined; }
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/trips/${trip.id}`);
        if (stop) return;
        setLiveHistory(Array.isArray(data.status_history) ? data.status_history : []);
        if (driverTouchedRef.current) return;
        const serverDriver = data.driver_id || "";
        setForm((f) => {
          if ((f.driver_id || "") === serverDriver) return f;
          const label = STATUS_LABELS[data.status] || data.status;
          setLiveDriverNote(serverDriver
            ? `${data.driver_name || "Chofer"} tomó este viaje mientras editabas · ${label}`
            : "El chofer fue quitado de este viaje mientras editabas");
          return { ...f, driver_id: serverDriver };
        });
      } catch { /* transient */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { stop = true; clearInterval(id); };
  }, [open, trip?.id]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const updUpper = (k) => (e) => setForm((f) => ({ ...f, [k]: (e.target?.value ?? e ?? "").toUpperCase() }));

  const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

  const isDelivery = form.trip_type === "delivery";

  // Async guard for the "stale coords" bug: when the dispatcher edits a
  // trip and swaps in a favourite that was saved without lat/lng, the
  // onPlace guard in the PlaceAutocomplete PRESERVES the previous trip's
  // coords rather than writing null. That's the right fallback while the
  // async geocoder is still running, but if the user hits "Guardar" fast
  // the PATCH ships the OLD address's coords under the NEW label — pin
  // stays put on the driver's map (RCA #20260914 field bug).
  //
  // Fix: right before shipping the PATCH/POST, if the address changed
  // relative to the trip we opened but the coords match the original,
  // force a synchronous geocode of the NEW address so pin and label align.
  const _resolveCoordsFor = async (address, currentLat, currentLng, originalAddress, originalLat, originalLng) => {
    if (!address || !address.trim()) return { lat: currentLat, lng: currentLng };
    const addrChanged = (address || "").trim() !== ((originalAddress || "").trim());
    const coordsStuck = originalLat != null
      && Math.abs((Number(currentLat) || 0) - Number(originalLat)) < 1e-6
      && Math.abs((Number(currentLng) || 0) - Number(originalLng || 0)) < 1e-6;
    // Only geocode when: address CHANGED but lat/lng didn't. If the user
    // already has fresh coords (typed a Google prediction), skip the call.
    if (!addrChanged || !coordsStuck) {
      return { lat: currentLat, lng: currentLng };
    }
    const g = window.google;
    if (!g?.maps?.Geocoder) return { lat: currentLat, lng: currentLng };
    try {
      const geocoder = new g.maps.Geocoder();
      const res = await new Promise((resolve, reject) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results?.[0]) resolve(results[0]);
          else reject(new Error(`geocode status=${status}`));
        });
      });
      const loc = res.geometry?.location;
      const rl = (l, k) => (l == null ? null : typeof l[k] === "function" ? l[k]() : l[k]);
      const lat = rl(loc, "lat");
      const lng = rl(loc, "lng");
      if (lat != null && lng != null) return { lat, lng };
    } catch (err) {
      console.warn("_resolveCoordsFor: geocode failed for", address, err);
    }
    // Give up — keep whatever the form had. Backend will store the address
    // with the stale coords but at least the dispatcher's next edit gets
    // another chance to fix it (the healer isn't backend-side anymore).
    return { lat: currentLat, lng: currentLng };
  };

  // Build the payload used by BOTH the initial submit and the "confirm
  // duplicate" flow. Kept as a closure so we always send exactly what the
  // dispatcher had on screen when they clicked Crear.
  const _buildPayload = () => {
    const p = {
      ...form,
      pickup_lat: numOrNull(form.pickup_lat),
      pickup_lng: numOrNull(form.pickup_lng),
      dropoff_lat: numOrNull(form.dropoff_lat),
      dropoff_lng: numOrNull(form.dropoff_lng),
      price: form.price === "" ? 0 : Number(form.price),
      driver_id: form.driver_id || null,
    // Derived accounting fields (driver_income / llc_income /
    // driver_collected) are NEVER sent from the form — the backend
    // recomputes them canonically from price·pct·payment_method·credit
    // on every PATCH. Sending stale $0 snapshots from the dialog was the
    // root cause of the "$5 con split $0/$0" bug on trip 20260831-076.
    driver_income: undefined,
    llc_income: undefined,
    driver_collected: undefined,
    driver_investment: numOrNull(form.driver_investment),
    client_debt_payment: numOrNull(form.client_debt_payment),
    client_credit_applied: numOrNull(form.client_credit_applied),
    lead_minutes: form.lead_minutes ? Number(form.lead_minutes) : null,
    // <input type="datetime-local"> returns a naive string ("2026-08-12T18:50")
    // in whatever timezone the dispatcher's browser thinks it is. That's a
    // problem for dispatchers working from OUTSIDE Naples — their browser
    // says CET/PST/etc, so "06:00" gets sent as "06:00 in their tz" instead
    // of "06:00 in Naples". The trip lands on the wrong day/hour on the
    // server. Force the interpretation to be Naples wallclock time.
    scheduled_at: form.scheduled_at
      ? naplesLocalToUtcISO(form.scheduled_at)
      : null,
    };
    // Editing without touching "Conductor": don't send driver_id at all, so a
    // driver assigned meanwhile by someone else is never overwritten.
    if (isEdit && !driverTouchedRef.current) delete p.driver_id;
    return p;
  };

  // Runs the actual POST /trips. Split out so we can call it either directly
  // (no duplicates) or after the dispatcher explicitly confirms.
  const _submitCreate = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips`, payload);
      if (data?.deferred) {
        toast.success(data.message || "Cita guardada");
      } else {
        toast.success("Viaje creado");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    // Since 2026-02 addresses are optional for every trip type — the dispatcher
    // may create quick trips with only phone + price. Backend also accepts
    // empty strings.
    // Bug guard (2026-09): resolve fresh coords for pickup/dropoff if the
    // address changed but the alias had no lat/lng — see _resolveCoordsFor.
    if (isEdit && trip) {
      const [pu, dr] = await Promise.all([
        _resolveCoordsFor(
          form.pickup_address, form.pickup_lat, form.pickup_lng,
          trip.pickup_address, trip.pickup_lat, trip.pickup_lng,
        ),
        _resolveCoordsFor(
          form.dropoff_address, form.dropoff_lat, form.dropoff_lng,
          trip.dropoff_address, trip.dropoff_lat, trip.dropoff_lng,
        ),
      ]);
      if (pu.lat !== form.pickup_lat || pu.lng !== form.pickup_lng ||
          dr.lat !== form.dropoff_lat || dr.lng !== form.dropoff_lng) {
        setForm((f) => ({
          ...f,
          pickup_lat: pu.lat, pickup_lng: pu.lng,
          dropoff_lat: dr.lat, dropoff_lng: dr.lng,
        }));
        // Feed the resolved coords into the payload directly — setForm is
        // async so `_buildPayload()` below wouldn't see them yet.
        form.pickup_lat = pu.lat;
        form.pickup_lng = pu.lng;
        form.dropoff_lat = dr.lat;
        form.dropoff_lng = dr.lng;
      }
    }
    const payload = _buildPayload();
    if (isEdit) {
      setBusy(true);
      try {
        await api.patch(`/trips/${trip.id}`, payload);
        // Auto-cleanup toast: if this trip was flagged as orphan and the fix
        // resolved every reason, announce it. If some issues remain, hint at
        // what's still off. Silent when there was nothing to fix.
        let announced = false;
        if (previousReasons.length > 0) {
          try {
            const { data: after } = await api.get(`/trips/${trip.id}/issues`);
            const remaining = Array.isArray(after?.reasons) ? after.reasons : [];
            const label = `#${trip.trip_number || after?.trip_number || "sin nº"}`;
            if (remaining.length === 0) {
              toast.success(`Viaje ${label} ya no aparece en el banner de inconsistentes`, { duration: 5000 });
            } else {
              toast.info(
                `Viaje ${label} sigue con ${remaining.length} motivo${remaining.length !== 1 ? "s" : ""} pendiente${remaining.length !== 1 ? "s" : ""}`,
                { duration: 5000 }
              );
            }
            announced = true;
          } catch (err) {
            console.debug("re-fetch issues after edit failed:", err?.message || err);
          }
        }
        if (!announced) toast.success("Viaje actualizado");
        onSaved?.();
        onOpenChange(false);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setBusy(false);
      }
      return;
    }

    // --- CREATE path: check for duplicate active trips on the same phone ---
    const phone = (form.passenger_phone || "").trim();
    if (phone) {
      setBusy(true);
      try {
        // "active" = anything that would show up in the dispatch panel: not
        // completed, not cancelled. Backend supports comma-separated statuses.
        const { data: existing } = await api.get("/trips", {
          params: {
            phone,
            status: "pending,assigned,accepted,arrived_pickup,with_client,pending_review",
          },
        });
        if (Array.isArray(existing) && existing.length > 0) {
          setBusy(false);
          setDupConfirm({ open: true, trips: existing });
          return; // wait for the dispatcher to confirm or cancel
        }
      } catch (err) {
        // Non-fatal: if the pre-check fails (network hiccup), we do NOT
        // block trip creation — just log and proceed.
        console.debug("duplicate-phone pre-check skipped:", err?.message || err);
      }
      setBusy(false);
    }
    await _submitCreate(payload);
  };

  const confirmCreateDuplicate = async () => {
    setDupConfirm({ open: false, trips: [] });
    await _submitCreate(_buildPayload());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card border-border max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0"
        data-testid="trip-form-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border/50 flex-shrink-0">
          <DialogTitle style={{ fontFamily: "Manrope" }} className="text-2xl flex items-center gap-2">
            {isDelivery ? <ShoppingCart className="w-6 h-6 text-accent" /> : <Car className="w-6 h-6 text-primary" />}
            {isEdit ? "Editar" : "Nuevo"} {isDelivery ? "delivery" : "viaje"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="trip-form-body">
        {/* Type toggle — also while editing: switching keeps phone, name and
            both addresses (Pickup→Restaurante, Drop off→Entrega). */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-secondary/40 rounded-md border border-border" data-testid="trip-type-toggle">
          <button
            type="button"
            onClick={() => setForm((f) => (f.trip_type === "trip" ? f : { ...f, trip_type: "trip", items: "" }))}
            data-testid="trip-type-trip"
            className={`flex items-center justify-center gap-2 py-2 rounded text-sm font-bold transition-colors ${
              !isDelivery
                ? "bg-primary text-primary-foreground shadow"
                : "text-muted-foreground hover:text-white"
            }`}
          >
            <Car className="w-4 h-4" /> Viaje
          </button>
          <button
            type="button"
            onClick={() => setForm((f) => (f.trip_type === "delivery" ? f : { ...f, trip_type: "delivery", notes: "" }))}
            data-testid="trip-type-delivery"
            className={`flex items-center justify-center gap-2 py-2 rounded text-sm font-bold transition-colors ${
              isDelivery
                ? "bg-accent text-accent-foreground shadow"
                : "text-muted-foreground hover:text-white"
            }`}
          >
            <ShoppingCart className="w-4 h-4" /> Delivery
          </button>
        </div>
        {isEdit && (trip?.trip_type || "trip") !== form.trip_type && (
          <div className="text-[11px] font-mono text-accent bg-accent/10 border border-accent/30 rounded px-2 py-1" data-testid="trip-type-changed-note">
            ⟳ Cambiarás este {trip?.trip_type === "delivery" ? "delivery a viaje" : "viaje a delivery"} al guardar. Teléfono, cliente y direcciones se conservan
            {form.trip_type === "delivery" ? " (Pickup → Restaurante, Drop off → Entrega). Las notas anteriores se eliminan; solo quedan los productos." : " (Restaurante → Pickup, Entrega → Drop off). Los productos se eliminan; solo quedan las notas."}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Pasajero</Label>
              <Input data-testid="trip-passenger-name" value={form.passenger_name} onChange={upd("passenger_name")} />
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label>Teléfono</Label>
                <button
                  type="button"
                  onClick={async () => {
                    const phone = (form.passenger_phone || "").trim();
                    if (!phone) {
                      toast.warning("Escribe primero el teléfono del pasajero");
                      return;
                    }
                    setHistoryPhone(phone);
                    setHistoryOpen(true);
                    setHistoryLoading(true);
                    setHistoryTrips([]);
                    try {
                      const { data } = await api.get(`/clients/${encodeURIComponent(phone)}/recent-trips?limit=5`);
                      setHistoryTrips(Array.isArray(data?.trips) ? data.trips : []);
                    } catch (e) {
                      toast.error(formatApiError(e));
                    } finally {
                      setHistoryLoading(false);
                    }
                  }}
                  data-testid="trip-quick-history-btn"
                  className="text-[10px] font-mono uppercase tracking-widest text-primary hover:text-primary/80 flex items-center gap-1"
                  title="Ver las últimas 3 carreras de este teléfono"
                >
                  <Clock className="w-3 h-3" /> Historial rápido
                </button>
              </div>
              <Input data-testid="trip-passenger-phone" value={form.passenger_phone} onChange={upd("passenger_phone")} />
            </div>
          </div>

          {!isEdit && nameConflicts.length > 0 && (
            <div
              data-testid="trip-name-conflict-alert"
              className="col-span-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
            >
              <div className="font-semibold text-amber-100 mb-1">
                Ya existe {nameConflicts.length === 1 ? "un cliente" : `${nameConflicts.length} clientes`} con este nombre:
              </div>
              <ul className="space-y-0.5 max-h-24 overflow-auto">
                {nameConflicts.slice(0, 5).map((c) => (
                  <li key={c.phone} className="font-mono">
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, passenger_phone: c.phone }))}
                      data-testid={`trip-name-conflict-use-${c.phone}`}
                      className="text-amber-300 hover:text-white underline"
                      title="Usar el teléfono de este cliente"
                    >
                      {c.passenger_name} · {c.phone}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[10px] text-amber-300/80">
                Tocá una fila para usar ese teléfono, o seguí escribiendo si es un cliente diferente.
              </div>
            </div>
          )}

          <div className="col-span-2 space-y-1">
            <div className="flex items-center justify-between">
              <Label>{isDelivery ? "Restaurante / Pickup (dirección)" : "Pickup (dirección)"}</Label>
              <button
                type="button"
                onClick={() => setManageOpen(true)}
                data-testid="manage-aliases-btn"
                className="text-[10px] font-mono uppercase tracking-widest text-primary hover:text-primary/80 flex items-center gap-1"
              >
                <Star className="w-3 h-3 fill-primary" /> Direcciones guardadas
              </button>
            </div>
            <PlaceAutocomplete
              value={form.pickup_address}
              onChange={(v) => setForm((f) => ({ ...f, pickup_address: v }))}
              onPlace={({ address, lat, lng, place_id }) =>
                // Guard: if geocode failed (lat==null), KEEP the previous
                // trip's coords instead of overwriting them with nulls.
                // `exclude_none=True` on the backend would then skip the
                // update — but the map pin would still be stale. This way
                // the pin either moves to the new location or stays put;
                // never lands on a null island.
                setForm((f) => ({
                  ...f,
                  pickup_address: address,
                  pickup_lat: lat != null ? lat : f.pickup_lat,
                  pickup_lng: lng != null ? lng : f.pickup_lng,
                  pickup_place_id: place_id || null,
                }))
              }
              aliases={aliases}
              testId="trip-pickup-address"
              placeholder={isDelivery ? "Ej: Domino's Pizza, 5th Ave S Naples" : "Ej: 5th Ave S, Naples FL, o nombre guardado"}
              className="bg-input border-border"
            />
          </div>
          {/* pickup/dropoff coords still auto-filled by PlaceAutocomplete for the map;
              manual coord inputs removed per user request. */}

          {/* Swap pickup ↔ dropoff (Google-Maps-style). Available on both modes
              now that delivery also uses a real pickup address (restaurant). */}
          <div className="col-span-2 -my-1 flex justify-end">
            <button
              type="button"
              data-testid="trip-swap-addresses"
              title="Intercambiar pickup y drop off"
              aria-label="Intercambiar pickup y drop off"
              onClick={() => setForm((f) => ({
                ...f,
                pickup_address: f.dropoff_address,
                pickup_lat: f.dropoff_lat,
                pickup_lng: f.dropoff_lng,
                pickup_place_id: f.dropoff_place_id,
                dropoff_address: f.pickup_address,
                dropoff_lat: f.pickup_lat,
                dropoff_lng: f.pickup_lng,
                dropoff_place_id: f.pickup_place_id,
              }))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-input/60 text-muted-foreground hover:text-primary hover:border-primary hover:bg-primary/10 active:scale-95 transition-all"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
          </div>

          <div className="col-span-2 space-y-1">
            <Label>{isDelivery ? "Dirección de entrega (opcional)" : "Drop off (dirección)"}</Label>
            <PlaceAutocomplete
              value={form.dropoff_address}
              onChange={(v) => setForm((f) => ({ ...f, dropoff_address: v }))}
              onPlace={({ address, lat, lng, place_id }) =>
                setForm((f) => ({
                  ...f,
                  dropoff_address: address,
                  dropoff_lat: lat != null ? lat : f.dropoff_lat,
                  dropoff_lng: lng != null ? lng : f.dropoff_lng,
                  dropoff_place_id: place_id || null,
                }))
              }
              aliases={aliases}
              testId="trip-dropoff-address"
              placeholder={isDelivery ? "Ej: casa del cliente, o dejar vacío" : "Ej: RSW Airport, o nombre guardado"}
              className="bg-input border-border"
            />
          </div>
          {/* dropoff coord inputs removed — PlaceAutocomplete keeps filling them internally */}

          <div className="space-y-1">
            <Label>Precio (USD)</Label>
            <Input data-testid="trip-price" type="number" step="0.01" value={form.price} onChange={upd("price")} />
          </div>
          <div className="space-y-1">
            <Label>Fecha/Hora <span className="text-[10px] font-mono uppercase tracking-widest text-primary/80">(Naples EDT)</span></Label>
            <Input data-testid="trip-scheduled-at" type="datetime-local" value={form.scheduled_at} onChange={upd("scheduled_at")} min={nowNaplesLocalString()} />
            <p className="text-[10px] text-muted-foreground">La hora siempre se guarda en hora de Naples, sin importar dónde esté tu computadora.</p>
          </div>

          {/* Lead-time dropdown: appears whenever the dispatcher has picked a
              scheduled_at. When a value is chosen (15/30/…) the trip is
              stored as a one-off appointment; picking "Crear ahora" bypasses
              that and creates a trip that appears in Dispatch immediately.
              The default is auto-set to 15 min when scheduled_at is in the
              future (see effect above), so a plain form submit becomes a
              Cita by default — matching the dispatcher's intent when they
              set a future time. */}
          {!isEdit && form.scheduled_at && (
            <div
              className={
                "col-span-2 space-y-1 rounded-md border p-3 " +
                (form.lead_minutes
                  ? "border-sky-500/40 bg-sky-500/5"
                  : "border-amber-500/50 bg-amber-500/10")
              }
            >
              <Label htmlFor="trip-lead-minutes" className={form.lead_minutes ? "text-sky-300" : "text-amber-200"}>
                {form.lead_minutes ? "Se guardará como cita" : "Va a bajar YA al panel"}
              </Label>
              <select
                id="trip-lead-minutes"
                data-testid="trip-lead-minutes"
                value={form.lead_minutes}
                onChange={upd("lead_minutes")}
                className="w-full bg-input border border-border rounded-md h-10 px-3 text-sm"
              >
                <option value="">⚡ Crear ahora (bajar inmediato al panel)</option>
                <option value="15">Guardar en Citas · bajar 15 min antes</option>
                <option value="20">Guardar en Citas · bajar 20 min antes</option>
                <option value="30">Guardar en Citas · bajar 30 min antes</option>
                <option value="45">Guardar en Citas · bajar 45 min antes</option>
                <option value="60">Guardar en Citas · bajar 60 min antes</option>
                <option value="90">Guardar en Citas · bajar 90 min antes</option>
                <option value="120">Guardar en Citas · bajar 120 min antes</option>
              </select>
              {form.lead_minutes ? (
                <div className="text-[10px] text-sky-300/80 leading-snug">
                  Va a la vista <b>Citas</b>. El chofer no lo verá hasta que falten {form.lead_minutes} min de la hora programada.
                </div>
              ) : (
                <div className="text-[10px] text-amber-200/90 leading-snug">
                  Con esta opción el viaje aparece <b>inmediatamente</b> en el panel de despacho aunque hayas puesto una hora futura. Si querías guardarlo como <b>Cita</b>, elegí "Guardar en Citas" arriba.
                </div>
              )}
            </div>
          )}

          <div className="space-y-1">
            <Label>Conductor</Label>
            {liveDriverNote && (
              <div className="text-[11px] font-mono text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1" data-testid="trip-live-driver-note">
                ⟳ {liveDriverNote}
              </div>
            )}
            <Select value={form.driver_id || "none"} onValueChange={(v) => { driverTouchedRef.current = true; setLiveDriverNote(""); setForm((f) => ({ ...f, driver_id: v === "none" ? "" : v })); }}>
              <SelectTrigger data-testid="trip-driver-select" className="bg-input border-border">
                <SelectValue placeholder="Sin asignar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {(() => {
                  // Business rule: a driver with the toggle in "Offline" no
                  // puede recibir viajes. Se ocultan del selector. Si el
                  // viaje YA estaba asignado a un chofer que ahora está
                  // offline (edición), lo mostramos igual para no romper la
                  // edición, pero marcado.
                  const assignable = (drivers || []).filter(
                    (d) => (d.driver_status || "offline") !== "offline",
                  );
                  const currentIsOffline =
                    form.driver_id &&
                    !assignable.some((d) => d.id === form.driver_id) &&
                    (drivers || []).some((d) => d.id === form.driver_id);
                  const showList = currentIsOffline
                    ? [
                        ...assignable,
                        (drivers || []).find((d) => d.id === form.driver_id),
                      ]
                    : assignable;
                  return showList.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name} · {d.driver_status || "offline"}
                    </SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Método de pago</Label>
            <Select value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
              <SelectTrigger data-testid="trip-payment-select" className="bg-input border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Efectivo</SelectItem>
                <SelectItem value="zelle">Zelle</SelectItem>
                <SelectItem value="credito">Crédito</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Delivery replaces the plain "Notas" textarea with "Productos / items"
              so the driver knows exactly what to pick up from the restaurant.
              For regular trips the block stays as free-form notes. */}
          <div className="col-span-2 space-y-1">
            <Label>{isDelivery ? "Productos / items" : "Notas"}</Label>
            {isDelivery ? (
              <Textarea
                data-testid="trip-items"
                value={form.items}
                onChange={updUpper("items")}
                placeholder="Ej: 2 PIZZAS GRANDES, 1 REFRESCO, 1 POSTRE..."
                rows={3}
                className="bg-input border-border uppercase"
              />
            ) : (
              <Textarea data-testid="trip-notes" value={form.notes} onChange={updUpper("notes")} rows={2} className="uppercase" />
            )}
          </div>

          {(isEdit || isDelivery) && (
            <div className="col-span-2 space-y-1 rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3">
              <Label htmlFor="trip-driver-investment" className="text-cyan-300">
                Inversión del chofer (USD)
              </Label>
              <Input
                id="trip-driver-investment"
                data-testid="trip-driver-investment"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={form.driver_investment}
                onChange={upd("driver_investment")}
                placeholder="0.00"
                className="bg-input border-border font-mono"
              />
              <div className="text-[10px] text-muted-foreground">
                Monto que el chofer gastó de su bolsillo (productos, peajes, gas). Se resta automáticamente de su deuda del día en Reportes.
              </div>
            </div>
          )}

          {/* Aplicar crédito del cliente — cuando el pasajero tiene saldo a
              favor con la LLC (balance < 0), el dispatcher puede consumir
              parte o todo ese crédito para cubrir este viaje. La LLC ya tiene
              ese dinero, así que se REDUCE lo que el chofer debe cobrar en
              efectivo/Zelle. Se registra como `charge` en el ledger para
              devolver el balance del cliente hacia cero. */}
          {(() => {
            const credit = clientSummary && clientSummary.balance < -0.01
              ? Math.abs(clientSummary.balance)
              : 0;
            const price = Number(form.price || 0);
            const maxApplicable = Math.min(credit, price);
            if (credit <= 0.01) return null;
            const applied = Number(form.client_credit_applied || 0);
            const netCollect = Math.max(0, price - applied);
            const remainingCredit = credit - applied;
            // When applied credit fully covers the price, flip the payment
            // method to "credito" so accounting mirrors Zelle (no cash flow,
            // driver never physically collects). If the user later reduces
            // the credit below the price, we drop back to cash automatically.
            const applyMax = () => setForm((f) => {
                const amt = maxApplicable.toFixed(2);
                const covers = maxApplicable >= (Number(f.price || 0) - 0.005);
                return {
                    ...f,
                    client_credit_applied: amt,
                    payment_method: covers ? "credito" : (f.payment_method === "credito" ? "cash" : f.payment_method),
                };
            });
            const clearApplied = () => setForm((f) => (
              { ...f, client_credit_applied: "", payment_method: f.payment_method === "credito" ? "cash" : f.payment_method }
            ));
            return (
              <div
                className="col-span-2 space-y-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3"
                data-testid="trip-client-credit-block"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label htmlFor="trip-client-credit" className="text-emerald-300">
                    Aplicar crédito del cliente
                  </Label>
                  <div className="text-[11px] text-emerald-300 font-mono" data-testid="trip-client-credit-available">
                    disponible ${credit.toFixed(2)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id="trip-client-credit"
                    data-testid="trip-client-credit-applied"
                    type="number"
                    step="0.01"
                    min="0"
                    max={maxApplicable}
                    inputMode="decimal"
                    value={form.client_credit_applied}
                    onChange={upd("client_credit_applied")}
                    placeholder="0.00"
                    className="bg-input border-border font-mono flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={applyMax}
                    disabled={maxApplicable <= 0.01}
                    data-testid="trip-apply-credit-max"
                    className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-bold whitespace-nowrap"
                    title={price > 0
                      ? `Aplicar ${maxApplicable.toFixed(2)} (mínimo entre crédito ${credit.toFixed(2)} y precio ${price.toFixed(2)})`
                      : "Ingresa un precio antes de aplicar"}
                  >
                    Aplicar ${maxApplicable.toFixed(2)}
                  </Button>
                  {applied > 0.01 && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={clearApplied}
                      className="text-muted-foreground hover:text-white"
                      data-testid="trip-apply-credit-clear"
                    >
                      Quitar
                    </Button>
                  )}
                </div>
                {applied > 0.01 && (
                  <div className="text-[11px] text-white/80 font-mono flex flex-wrap gap-x-3 gap-y-1" data-testid="trip-client-credit-preview">
                    <span>chofer cobra <span className="text-emerald-300">${netCollect.toFixed(2)}</span></span>
                    <span>crédito restante <span className="text-emerald-300">${Math.max(0, remainingCredit).toFixed(2)}</span></span>
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground leading-snug">
                  El chofer recibe menos efectivo/Zelle porque la LLC ya tenía este monto del cliente. Su comisión no cambia. Al cancelar el viaje se devuelve el crédito automáticamente.
                </div>
              </div>
            );
          })()}

          {/* Cobrado Chofer — money the driver collects from the passenger on
              top of the trip price to settle a previous LLC debt. NEVER counted
              as driver production; ALWAYS added to what he owes the LLC. */}
          <div className="col-span-2 space-y-1 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
            <Label htmlFor="trip-client-debt" className="text-amber-300">
              Cobrado Chofer (deuda LLC — USD)
            </Label>
            <Input
              id="trip-client-debt"
              data-testid="trip-client-debt-payment"
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={form.client_debt_payment}
              onChange={upd("client_debt_payment")}
              placeholder="0.00"
              className="bg-input border-border font-mono"
            />
            <div className="text-[10px] text-muted-foreground">
              Dinero extra que el chofer le cobra al cliente para saldar deuda con la LLC. NO cuenta en su producción; suma a lo que debe entregar hoy. Se vincula al teléfono del pasajero.
            </div>
            {clientSummary && (
              <div
                className={`mt-1 text-[11px] font-mono flex items-center gap-2 ${
                  clientSummary.balance > 0.01 ? "text-red-400" :
                  clientSummary.balance < -0.01 ? "text-emerald-400" :
                  "text-muted-foreground"
                }`}
                data-testid="trip-client-balance"
              >
                <span className="uppercase tracking-widest text-[9px] text-muted-foreground">Cliente</span>
                {clientSummary.balance > 0.01 && (
                  <span>Debe ${clientSummary.balance.toFixed(2)}</span>
                )}
                {clientSummary.balance < -0.01 && (
                  <span>Crédito ${Math.abs(clientSummary.balance).toFixed(2)}</span>
                )}
                {Math.abs(clientSummary.balance) <= 0.01 && (
                  <span>Sin saldo pendiente</span>
                )}
                {(() => {
                  const paid = Number(form.client_debt_payment || 0);
                  if (paid <= 0) return null;
                  const newBal = (clientSummary.balance || 0) - paid;
                  return (
                    <span className="text-white/80">
                      → tras este cobro:{" "}
                      {newBal > 0.01 && (<span className="text-amber-300">debe ${newBal.toFixed(2)}</span>)}
                      {newBal < -0.01 && (<span className="text-emerald-300">crédito ${Math.abs(newBal).toFixed(2)}</span>)}
                      {Math.abs(newBal) <= 0.01 && (<span className="text-white">saldo cero</span>)}
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
        {isEdit && (
          <TripMovementsSection history={liveHistory || []} loading={liveHistory === null} />
        )}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-border/50 flex-shrink-0 bg-card">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="trip-form-cancel">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
            data-testid="trip-form-submit"
          >
            {busy ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear viaje"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <AddressAliasesDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        onAliasesChanged={loadAliases}
      />
      {/* Duplicate-phone guard. Shown ONLY on create when there's already at
          least one active trip on the same phone. The dispatcher can either
          confirm (Sí, crear igual) or cancel and stay on the form. */}
      <AlertDialog
        open={dupConfirm.open}
        onOpenChange={(o) => !o && setDupConfirm({ open: false, trips: [] })}
      >
        <AlertDialogContent data-testid="trip-duplicate-phone-dialog" className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Ya hay un viaje activo con este teléfono</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  El número <span className="font-mono text-white">{form.passenger_phone}</span> ya tiene {dupConfirm.trips.length}{" "}
                  {dupConfirm.trips.length === 1 ? "viaje activo" : "viajes activos"} en el panel de despacho:
                </p>
                <ul className="space-y-1 max-h-40 overflow-auto rounded border border-border bg-input/40 p-2">
                  {dupConfirm.trips.map((t) => (
                    <li key={t.id} className="font-mono text-xs text-muted-foreground">
                      <span className="text-primary">#{t.trip_number || t.id.slice(-6)}</span> · {t.passenger_name || "s/nombre"} · {t.status}
                    </li>
                  ))}
                </ul>
                <p>¿Seguro que quieres crear otro viaje para este mismo cliente?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trip-duplicate-phone-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="trip-duplicate-phone-confirm"
              onClick={confirmCreateDuplicate}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Sí, crear igual
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick-history popover — last 3 completed trips for the passenger
          phone. Read-only reference so the dispatcher can eyeball recurring
          pickup/drop-off patterns and price without leaving the form. */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent
          data-testid="trip-quick-history-dialog"
          className="bg-card border-border max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4 text-primary" />
              Historial rápido — {historyPhone}
            </DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Cargando…</div>
          ) : historyTrips.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Este teléfono no tiene carreras completadas todavía.
            </div>
          ) : (
            <ul className="space-y-2 max-h-[70vh] overflow-auto">
              {historyTrips.map((t) => (
                <li
                  key={t.id}
                  data-testid={`quick-history-item-${t.id}`}
                  className="rounded-md border border-border bg-input/40 p-2 text-xs space-y-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-primary">#{t.trip_number || t.id.slice(-6)}</span>
                    <span className="font-mono font-black" style={{ color: "#FBBF24" }}>
                      ${Number(t.price || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="text-white/90 truncate" title={t.pickup_address}>
                    <span className="text-muted-foreground">Pickup:</span>{" "}
                    {t.pickup_alias
                      ? <><span className="text-primary font-bold" data-testid={`quick-history-pickup-alias-${t.id}`}>★ {t.pickup_alias}</span> <span className="text-muted-foreground text-[10px]">· {t.pickup_address}</span></>
                      : (t.pickup_address || "—")}
                  </div>
                  <div className="text-white/90 truncate" title={t.dropoff_address}>
                    <span className="text-muted-foreground">Drop:</span>{" "}
                    {t.dropoff_alias
                      ? <><span className="text-primary font-bold" data-testid={`quick-history-dropoff-alias-${t.id}`}>★ {t.dropoff_alias}</span> <span className="text-muted-foreground text-[10px]">· {t.dropoff_address}</span></>
                      : (t.dropoff_address || "—")}
                  </div>
                  <div className="flex items-center justify-between gap-2 text-muted-foreground">
                    <span>Chofer: <span className="text-white/80">{t.driver_name || "—"}</span></span>
                    <span className="uppercase text-[10px] font-mono">{t.payment_method}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setHistoryOpen(false)}
              data-testid="trip-quick-history-close"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
